import React, { useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { useSentry } from '../hooks/useSentry';
import './SentryErrorBoundary.scss';
import { logger } from '../utils/logger';

// Define the fallback component
const ErrorFallback: Sentry.FallbackRender = (errorData) => (
  <div className="error-boundary">
    <div className="error-container">
      <h2>Something went wrong</h2>
      <p>{errorData.error?.toString() || 'An error occurred'}</p>
      <button 
        onClick={errorData.resetError}
        className="btn btn-primary"
      >
        Try again
      </button>
    </div>
  </div>
);

interface SentryErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: Sentry.FallbackRender;
  showDialog?: boolean;
}

export const SentryErrorBoundary: React.FC<SentryErrorBoundaryProps> = ({ 
  children, 
  fallback = ErrorFallback,
  showDialog = false
}) => {
  // State to track if the component has mounted
  const [isMounted, setIsMounted] = useState(false);
  
  // Use a try-catch block to safely use the Sentry context
  let isTracked = false;
  let trackingLevel: string | null = null;
  let isLoading = false;
  let contextAvailable = false;
  
  try {
    // Try to use the Sentry context
    const context = useSentry();
    isTracked = context.isTracked;
    trackingLevel = context.trackingLevel;
    isLoading = context.isLoading;
    contextAvailable = true;
  } catch (error) {
    // If SentryContext is not available, just log it and continue
    logger.info('SentryContext not available yet, rendering children without error boundary');
    contextAvailable = false;
  }
  
  // Set mounted state after component mounts
  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);
  
  // Only capture errors if user is being tracked
  const beforeCapture = (scope: Sentry.Scope) => {
    try {
      // Don't capture if not tracked
      if (!isTracked) {
        logger.debug('Sentry tracking disabled for this user, not capturing error');
        return false;
      }
      
      // Set level based on tracking level
      if (trackingLevel === 'basic') {
        // Only capture errors
        scope.setLevel('error');
      } else if (trackingLevel === 'detailed') {
        // Capture errors and warnings
        scope.setLevel('warning');
      } else if (trackingLevel === 'full') {
        // Capture everything
        scope.setLevel('log');
      } else {
        // Default to errors only
        scope.setLevel('error');
      }
      
      // Add additional context
      scope.setTag('component', 'SentryErrorBoundary');
      scope.setTag('url', window.location.href);
      scope.setTag('tracking_level', trackingLevel || 'basic');
      
      // Add browser information
      scope.setContext('browser', {
        userAgent: navigator.userAgent,
        language: navigator.language,
        windowSize: `${window.innerWidth}x${window.innerHeight}`,
        screenSize: `${window.screen.width}x${window.screen.height}`,
        devicePixelRatio: window.devicePixelRatio
      });
      
      return true;
    } catch (error) {
      logger.error("Error in beforeCapture:", error);
      // Default to capturing the error if there's an error in the beforeCapture function
      return true;
    }
  };
  
  // Create a custom error handler for the ErrorBoundary
  const onError = (error: unknown, componentStack: string, eventId: string) => {
    logger.error("Caught error in SentryErrorBoundary:", error);
    logger.error("Component stack:", componentStack);
    logger.error("Event ID:", eventId);
    
    // Log additional information to help with debugging
    logger.error("Current URL:", window.location.href);
    logger.error("User agent:", navigator.userAgent);
    
    // Automatically track users who experience errors
    if (!isTracked && isMounted) {
      try {
        // Add a tag to indicate this was an auto-tracked error
        Sentry.withScope(scope => {
          scope.setTag('auto_tracked', 'true');
          scope.setTag('error_id', eventId);
          
          // Capture the error again with the auto-tracked tag
          Sentry.captureException(error);
        });
        
        logger.info('Auto-tracking user due to error');
      } catch (trackingError) {
        logger.error('Error auto-tracking user:', trackingError);
      }
    }
  };
  
  // Use try-catch to handle any errors in the ErrorBoundary itself
  try {
    // The boundary stays in the tree whatever the tracking context is doing. Returning
    // children bare in one branch and wrapped in the other moves them to a different
    // position in the element tree, and React answers that by throwing the whole subtree
    // away and mounting a fresh one - the entire app below here, every time the tracking
    // queries flipped isLoading, which they do on a poll. Measured in a browser as the
    // lesson library and its fourteen grade cards vanishing for a couple of seconds while
    // every provider above stayed mounted.
    //
    // While the context is unavailable or still loading there is nothing to report an
    // error to, so the boundary simply passes errors through instead of being removed.
    const canReport = contextAvailable && !isLoading;

    return (
      <Sentry.ErrorBoundary
        fallback={canReport ? fallback : ((errorData: any) => { throw errorData.error; }) as Sentry.FallbackRender}
        beforeCapture={canReport ? beforeCapture : undefined}
        onError={canReport ? onError : undefined}
        showDialog={canReport ? showDialog : false}
      >
        {children}
      </Sentry.ErrorBoundary>
    );
  } catch (error) {
    logger.error("Error rendering Sentry ErrorBoundary:", error);
    // Return children directly if ErrorBoundary fails
    return <>{children}</>;
  }
};
